import React from 'react';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import {
  FormAccordionItem,
  FormAccordionItemContainer,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Box, Text, Button, Group, Tooltip } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { FormTable } from 'src/common/components/form-table/FormTable';
import { FeedInfo } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import { IconPlus, IconArrowsExchange } from '@tabler/icons-react';
import { FundCodeDropdown } from './FundCodeDropdown';
import DynamicField from 'src/UI/dynamicUI/layout/DynamicField';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

export const FUND_HOLDING = 'FundHolding';

interface FundHoldingData {
  id?: string | null;
  units_held?: number | null;
  holding_value?: number | null;
  value_date?: string | null;
  fund_id?: string | null;
  fund?: {
    id?: string;
    name?: string;
    code?: string;
    price_per_unit?: number;
  } | null;
  original_currency_code?: string | null;
  original_price_per_unit?: number | null;
  original_value?: number | null;
  fx_rate_used?: number | null;
  fx_rate_date?: string | null;
}

type FundHoldingValueCellProps = {
  index: number;
  holding: FundHoldingData;
  canEdit: boolean;
};

const FundHoldingValueCell = ({ index, holding, canEdit }: FundHoldingValueCellProps) => {
  const hasConversion = !!holding?.original_currency_code;

  return (
    <Group gap={6} wrap="nowrap">
      <DynamicField
        field={{
          type: 'FIELD',
          span: 12,
          entityType: FUND_HOLDING,
          entityField: 'holding_value',
        }}
        disabled={!canEdit}
        index={index}
        hideLabel
      />
      {hasConversion && (
        <Tooltip
          label={
            <Box>
              <Text size="xs" fw={FONT_WEIGHTS.semibold}>
                Currency Conversion
              </Text>
              <Text size="xs" mt="xxxs">
                Original value: {holding.original_currency_code}{' '}
                {typeof holding.original_value === 'number'
                  ? holding.original_value.toLocaleString('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : holding.original_value}
              </Text>
              <Text size="xs">
                Rate: {holding.fx_rate_used} {holding.original_currency_code} → GBP
              </Text>
              {holding.fx_rate_date && (
                <Text size="xs">
                  Date: {new Date(holding.fx_rate_date as string).toLocaleDateString('en-GB')}
                </Text>
              )}
            </Box>
          }
          position="bottom-start"
          withArrow
          multiline
          w={220}
        >
          <Box
            style={{
              cursor: 'help',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconArrowsExchange size={18} style={{ color: 'var(--mantine-color-yellow-7)' }} />
          </Box>
        </Tooltip>
      )}
    </Group>
  );
};

type FundHoldingPricePerUnitCellProps = {
  index: number;
  holding: FundHoldingData;
};

const FundHoldingPricePerUnitCell = ({ index, holding }: FundHoldingPricePerUnitCellProps) => {
  const hasConversion = !!holding?.original_currency_code;

  return (
    <Group gap={6} wrap="nowrap">
      <DynamicField
        field={{
          type: 'FIELD',
          span: 12,
          entityType: FUND_HOLDING,
          entityField: 'fund.price_per_unit',
        }}
        disabled={true}
        index={index}
        hideLabel
      />
      {hasConversion && (
        <Tooltip
          label={
            <Box>
              <Text size="xs" fw={FONT_WEIGHTS.semibold}>
                Currency Conversion
              </Text>
              <Text size="xs" mt="xxxs">
                Original price: {holding.original_currency_code}{' '}
                {typeof holding.original_price_per_unit === 'number'
                  ? holding.original_price_per_unit.toLocaleString('en-GB', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : holding.original_price_per_unit}
              </Text>
              <Text size="xs">
                Rate: {holding.fx_rate_used} {holding.original_currency_code} → GBP
              </Text>
              {holding.fx_rate_date && (
                <Text size="xs">
                  Date: {new Date(holding.fx_rate_date as string).toLocaleDateString('en-GB')}
                </Text>
              )}
            </Box>
          }
          position="bottom-start"
          withArrow
          multiline
          w={220}
        >
          <Box
            style={{
              cursor: 'help',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconArrowsExchange size={18} style={{ color: 'var(--mantine-color-yellow-7)' }} />
          </Box>
        </Tooltip>
      )}
    </Group>
  );
};

type FundHoldingFormAccordionItemListProps = {
  isEditing: boolean;
  form: UseFormReturnType<FormValuesType>;
  productType: string;
  titleIcon?: React.ReactNode;
  currencyIcon?: React.ReactNode;
  feedInfo?: FeedInfo;
};

export const FundHoldingFormAccordionItemList = ({
  form,
  isEditing,
  productType,
  titleIcon,
  currencyIcon,
  feedInfo,
}: FundHoldingFormAccordionItemListProps) => {
  const untypedItems = form.getValues()[FUND_HOLDING];
  const fundHoldings = Array.isArray(untypedItems) ? untypedItems : [];
  const formValues = form.getValues();
  const productEntity = formValues[productType] as Record<string, unknown>;
  const providerReferenceNumber = productEntity?.provider_reference_number;
  const hasReferenceNumber =
    typeof providerReferenceNumber === 'string' && providerReferenceNumber.trim() !== '';
  const hasNoItems = fundHoldings.length === 0;

  const isConnectedToFeed = feedInfo?.isUpdatedAutomatically ?? false;
  const canEdit = isEditing && !isConnectedToFeed;

  const handleAddFundHolding = () => {
    form.insertListItem(FUND_HOLDING, { id: null, type: null, amount: null, fund_id: null });
  };

  const pricePerUnitTooltip = 'Price per unit is filled automatically when the asset is saved.';
  const descriptionTooltip = 'Description is filled automatically from the selected fund.';

  const accordionHeaderIcon =
    titleIcon || currencyIcon ? (
      <Group gap={4}>
        {titleIcon}
        {currencyIcon}
      </Group>
    ) : undefined;

  if (hasNoItems)
    return (
      <FundHoldingFormAccordionItemListNoItems
        form={form}
        productType={productType}
        titleIcon={accordionHeaderIcon}
        canEdit={canEdit}
        onAddFundHolding={handleAddFundHolding}
      />
    );

  const tableContent = (
    <FormTable
      columns={[
        {
          accessor: 'fund.name',
          title: 'Description',
          disabled: true,
          tooltip: canEdit ? descriptionTooltip : undefined,
        },
        {
          accessor: 'fund.code',
          title: 'Fund code',
          customRender: (index: number) => (
            <FundCodeDropdown
              form={form}
              formFieldName={`${FUND_HOLDING}.${index}.fund_id`}
              fieldName="fund_id"
              disabled={!canEdit}
              editMode={canEdit}
            />
          ),
        },
        {
          accessor: 'fund.price_per_unit',
          title: 'Price per unit',
          tooltip: canEdit ? pricePerUnitTooltip : undefined,
          customRender: (index: number) => {
            const holding = fundHoldings[index] as FundHoldingData;
            return <FundHoldingPricePerUnitCell index={index} holding={holding} />;
          },
        },
        {
          accessor: 'value_date',
          title: 'Valuation date',
        },
        {
          accessor: 'units_held',
          title: 'Units held',
        },
        {
          accessor: 'holding_value',
          title: 'Value',
          customRender: (index: number) => {
            const holding = fundHoldings[index] as FundHoldingData;
            return <FundHoldingValueCell index={index} holding={holding} canEdit={canEdit} />;
          },
        },
      ]}
      dataKey={FUND_HOLDING}
      form={form}
      showDeleteButton={canEdit}
    />
  );

  return (
    <FormAccordionItem
      value="fund_holdings"
      label="Fund holdings"
      childrenWrapper={null}
      titleIcon={accordionHeaderIcon}
    >
      <>
        <FormAccordionItemContainer>
          {isConnectedToFeed ? <ReadOnlyWrapper>{tableContent}</ReadOnlyWrapper> : tableContent}
        </FormAccordionItemContainer>
        {canEdit && (
          <Button
            mt="md"
            variant="secondary"
            onClick={handleAddFundHolding}
            leftSection={<IconPlus size={16} />}
            data-testid="add-fund-holding-button"
          >
            Add fund holding
          </Button>
        )}
        {hasReferenceNumber && isConnectedToFeed && (
          <Text mt="md">{`Populated from feed (Ref #${providerReferenceNumber})`}</Text>
        )}
      </>
    </FormAccordionItem>
  );
};

const FundHoldingFormAccordionItemListNoItems = ({
  form,
  productType,
  titleIcon,
  canEdit,
  onAddFundHolding,
}: {
  form: UseFormReturnType<FormValuesType>;
  productType: string;
  titleIcon?: React.ReactNode;
  canEdit?: boolean;
  onAddFundHolding?: () => void;
}) => {
  const formValues = form.getValues();
  const productEntity = formValues[productType] as Record<string, unknown>;
  const providerReferenceNumber = productEntity?.provider_reference_number;
  const hasReferenceNumber =
    typeof providerReferenceNumber === 'string' && providerReferenceNumber.trim() !== '';
  const getMessage = () => {
    if (hasReferenceNumber) {
      return `No funds are linked to the entered provider reference number (${providerReferenceNumber}). Please check if the number has been entered correctly.${canEdit ? ' You can also add fund holdings manually below.' : ''}`;
    }
    return canEdit
      ? 'Enter the provider reference number (in summary above) to link with the provider and allow an automatic feed of underlying fund holdings here, or add fund holdings manually below.'
      : 'Enter the provider reference number (in summary above) to link with the provider and allow an automatic feed of underlying fund holdings here.';
  };

  return (
    <FormAccordionItem
      value="fund_holdings"
      label="Fund holdings"
      childrenWrapper={null}
      titleIcon={titleIcon}
    >
      <FormAccordionItemContainer>
        <Box w="35%" mx="auto">
          <Text ta="center" c="dimmed">
            {getMessage()}
          </Text>
        </Box>
      </FormAccordionItemContainer>
      {canEdit && onAddFundHolding && (
        <Button
          mt="md"
          variant="secondary"
          onClick={onAddFundHolding}
          leftSection={<IconPlus size={16} />}
          data-testid="add-fund-holding-button"
        >
          Add fund holding
        </Button>
      )}
    </FormAccordionItem>
  );
};
